import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Save } from "lucide-react";
import ChangePasswordCard from "../../components/ChangePasswordCard";
import PushNotificationCard from "../../components/PushNotificationCard";
import ApiKeyCard from "../../components/ApiKeyCard";

export default function CoachProfile() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", bio: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        bio: user.bio || "",
      });
    }
  }, [user?.user_id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        bio: form.bio,
      });
      await refreshMe();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="coach-profile-page">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Account</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">Your profile</h1>
        <p className="text-sm text-[#A3A3A3] mt-3">
          Students see your name and bio when choosing a coach — keep it sharp.
        </p>
      </div>

      <form onSubmit={save} className="card-surface p-8 space-y-6">
        <Field label="Full name" value={form.name} onChange={set("name")} required testId="coach-profile-name" />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} required testId="coach-profile-email" />
        <Field label="Phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" testId="coach-profile-phone" />

        <div>
          <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Bio (shows on the student coach-picker)</label>
          <textarea
            rows={4} value={form.bio} onChange={set("bio")}
            placeholder="PPR-certified since 2019, focus on kitchen strategy and 3rd-shot drops…"
            className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm mt-1"
            data-testid="coach-profile-bio"
          />
        </div>

        <button disabled={saving} className="btn-primary py-3 px-6 text-xs inline-flex items-center gap-2" data-testid="coach-profile-save">
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </form>

      <ChangePasswordCard />
      <ApiKeyCard />
      <PushNotificationCard />
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <input
        type={type} value={value} onChange={onChange} required={required} placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
        data-testid={testId}
      />
    </div>
  );
}
